import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { Switch } from "./ui/switch";
import { getCookieConsent, saveCookieConsent } from "../lib/consent";

const CATS = [
  { key: "necessary", label: "Strictly necessary", desc: "Login, session, security, checkout. Always on.", locked: true },
  { key: "functional", label: "Functional / preferences", desc: "Language and settings." },
  { key: "analytics", label: "Analytics", desc: "Aggregate usage statistics." },
  { key: "marketing", label: "Marketing", desc: "If used, for promotional communications." },
];

export const CookieBanner = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [customize, setCustomize] = useState(false);
  const [prefs, setPrefs] = useState({ functional: false, analytics: false, marketing: false });

  useEffect(() => {
    if (!getCookieConsent()) setOpen(true);
    const reopen = () => {
      const c = getCookieConsent();
      if (c) setPrefs({ functional: !!c.functional, analytics: !!c.analytics, marketing: !!c.marketing });
      setCustomize(true);
      setOpen(true);
    };
    window.addEventListener("agz-open-cookie-settings", reopen);
    return () => window.removeEventListener("agz-open-cookie-settings", reopen);
  }, []);

  if (!open || location.pathname.startsWith("/l/")) return null;

  const done = (choices) => {
    saveCookieConsent(choices);
    setOpen(false);
    setCustomize(false);
  };

  const btn = "flex-1 sm:flex-none sm:min-w-[130px] rounded-full py-2.5 px-5 text-[13px] font-semibold bg-[#181621] text-[#FAF8F3] hover:opacity-90 transition-opacity";

  return (
    <div className="fixed inset-x-0 bottom-0 z-[100] px-3 pb-3 sm:px-5 sm:pb-5" data-testid="cookie-banner">
      <div className="max-w-3xl mx-auto card-soft bg-white p-5 sm:p-6 shadow-[0_16px_48px_rgba(24,22,33,0.18)]">
        <p className="font-mono-j text-[10px] tracking-[0.14em] text-[#54505B] uppercase">COOKIE</p>
        <p className="mt-2 text-[13.5px] text-[#3F3E3A] leading-relaxed">
          We use strictly necessary cookies to run Agenzee and, only with your consent, functional, analytics and marketing cookies.
          No non-essential cookie is set before you choose.{" "}
          <Link to="/cookie" className="underline underline-offset-2 text-[#181621]" data-testid="cookie-banner-policy-link">Cookie Policy</Link>
        </p>

        {customize && (
          <div className="mt-4 space-y-3 border-t border-[#F3EFE7] pt-4" data-testid="cookie-customize-panel">
            {CATS.map((c) => (
              <div key={c.key} className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-[13px] font-semibold text-[#181621]">{c.label}</p>
                  <p className="text-[11.5px] text-[#9A9591]">{c.desc}</p>
                </div>
                <Switch
                  checked={c.locked ? true : prefs[c.key]}
                  disabled={c.locked}
                  onCheckedChange={(v) => setPrefs((p) => ({ ...p, [c.key]: v }))}
                  data-testid={`cookie-toggle-${c.key}`}
                />
              </div>
            ))}
          </div>
        )}

        <div className="mt-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5">
          {customize ? (
            <button className={btn} onClick={() => done(prefs)} data-testid="cookie-save-prefs">Save preferences</button>
          ) : (
            <>
              <button className={btn} onClick={() => done({ functional: true, analytics: true, marketing: true })} data-testid="cookie-accept-all">
                Accept all
              </button>
              <button className={btn} onClick={() => done({ functional: false, analytics: false, marketing: false })} data-testid="cookie-reject-all">
                Reject all
              </button>
            </>
          )}
          <button
            className="sm:ml-auto text-[12.5px] font-medium text-[#54505B] underline underline-offset-2 py-2"
            onClick={() => setCustomize((c) => !c)}
            data-testid="cookie-customize-btn"
          >
            {customize ? "Back" : "Customize"}
          </button>
        </div>
      </div>
    </div>
  );
};
